import * as React from 'react';
import isNil from 'lodash/isNil';

import { Form, FormActions, FormField } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';
import InputToggles from 'components/inputs/input-toggles/InputToggles';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { __t, applyTranslations } from 'utils/language';
import {
  ETHNIC_GROUP_OPTIONS_PROFILE_BOOST,
  FASHION_CATEGORIES,
  FASHION_SIZES,
  FASHION_STYLES
} from 'utils/influencers';

import './profile-boost-data.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./profile-boost-data.i18n.json'));

import type {
  Data,
  InfluencerProfileForm,
  MixedSelectDropdownItem
} from 'types';
import type {
  FieldDescriptionShowParams
} from 'modules/influencers/components/influencer-create-profile/InfluencerCreateProfile';

type Items = Array<{
  label: string,
  value: string
}>;

interface Props {
  details: InfluencerProfileForm,
  onSubmit: (details: InfluencerProfileForm) => void,
  onCancel: (details: InfluencerProfileForm) => void,
  onFieldDescriptionShow: (params: FieldDescriptionShowParams) => void,
  onFieldDescriptionHide: () => void
}

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'profile_boost_data';

const ProfileBoostData: React.FC<Props> = ({
  details,
  onSubmit,
  onCancel,
  onFieldDescriptionShow,
  onFieldDescriptionHide
}): JSX.Element => {
  const [updatedDetails, setUpdatedDetails] = React.useState(details);

  const getFashionSizesHelperText = (): React.ReactNode => {
    const sharedMsgKey = `${MSG_KEY}.fashion_sizes.helper_text`;

    const getText = (key: string): React.ReactNode => (
      <div>
        <strong>
          {__t(`${sharedMsgKey}.${key}`)}
        </strong>:&nbsp;
        <span>
          {__t(`${sharedMsgKey}.${key}_text`)}
        </span>
      </div>
    );

    return (
      <div className="profile-boost-data__helper-text">
        {getText('adaptive')}
        {getText('petite')}
        {getText('plus_size')}
        {getText('standard_manufacturer_sizing')}
        {getText('tall')}
      </div>
    );
  };

  const getEthnicGroupsHelperText = (): React.ReactNode => {
    const getText = (key: string): React.ReactNode => (
      <div>
        {__t(`${MSG_KEY}.ethnic_groups.helper_text.${key}`)}
      </div>
    );

    return (
      <div className="profile-boost-data__helper-text">
        {getText('block_one')}
        {getText('block_two')}
        {getText('block_three')}
        {getText('block_four')}
      </div>
    );
  };

  const handleInputTogglesUpdate = (
    values: Array<string>,
    data: Data
  ): void => {
    setUpdatedDetails({
      ...updatedDetails,
      [data.type]: values
    });
  };

  const handleDropdownUpdate = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null
  ): void => {
    if (isNil(data)) {
      return;
    }

    const selectedItem = updatedItems.find(item => item.isSelected);
    const type = data.type;

    setUpdatedDetails({
      ...updatedDetails,
      [type]: !isNil(selectedItem)
        ? selectedItem.identifier
        : updatedDetails[type]
    });

    onFieldDescriptionHide();
  };

  const handleFieldDescriptionShow = (
    name: 'fashionSizes' | 'ethnicGroups'
  ): void => {
    const positionTop = name === 'fashionSizes'
      ? 300
      : 478;

    onFieldDescriptionShow({
      name,
      positionTop,
      width: 500,
      component: name === 'fashionSizes'
        ? getFashionSizesHelperText()
        : getEthnicGroupsHelperText()
    });
  };

  const handleSubmit = (): void => (
    onSubmit(updatedDetails)
  );

  const handleCancel = (): void => (
    onCancel(updatedDetails)
  );

  const renderInputToggles = (
    type: 'fashionStyles' | 'fashionCategories' | 'fashionSizes',
    items: Items,
    limit: number = 3
  ): React.ReactNode => (
    <InputToggles
      key={`profile-boost-data__input-toggles--${type}`}
      items={items}
      value={updatedDetails[type] || []}
      limit={limit}
      onChange={handleInputTogglesUpdate}
      data={{ type }}
    />
  );

  const renderFashionStyles = (): React.ReactNode => {
    const items = FASHION_STYLES.map(value => ({
      value,
      label: __t(`collabary_common.fashion_styles.${value}`)
    }));

    return (
      <div className="profile-boost-data__section">
        {renderInputToggles('fashionStyles', items)}
      </div>
    );
  };

  const renderFashionCategories = (): React.ReactNode => {
    const items = FASHION_CATEGORIES.map(value => ({
      value,
      label: __t(`collabary_common.fashion_categories.${value}`)
    }));

    return renderInputToggles('fashionCategories', items);
  };

  const renderFashionSizes = (): React.ReactNode => {
    const items = FASHION_SIZES.map(value => ({
      value,
      label: __t(`collabary_common.fashion_sizes.${value}`)
    }));

    return renderInputToggles('fashionSizes', items, 3);
  };

  const renderEthnicGroups = (): React.ReactNode => {
    const items = ETHNIC_GROUP_OPTIONS_PROFILE_BOOST.map(value => ({
      identifier: value,
      label: __t(`${COMMON_MSG_KEY}.ethnic_group.${value}`),
      value,
      isSelected: updatedDetails.ethnicGroup === value,
      isDisabled: false
    }));

    return (
      <div className="profile-boost-data__ethnic-groups">
        <MixedSelectDropdown
          key="profile-boost-data__ethnic-groups-dropdown"
          name="profile-boost-data__ethnic-groups-dropdown"
          items={items}
          placeholder={__t(`${COMMON_MSG_KEY}.basics.choose_one`)}
          className="profile-boost-data__dropdown"
          withSelectedInList
          onItemsToggle={handleDropdownUpdate}
          data={{ type: 'ethnicGroup' }}
        />
      </div>
    );
  };

  const renderActions = (): React.ReactNode => (
    <FormActions align="center">
      <ActionButton
        color="tertiary"
        onClick={handleCancel}
      >
        {__t(`${COMMON_MSG_KEY}.action_button.back`)}
      </ActionButton>
      <ActionButton
        color="primary"
        type="submit"
      >
        {__t(`${COMMON_MSG_KEY}.action_button.continue`)}
      </ActionButton>
    </FormActions>
  );

  return (
    <div className="profile-boost-data">
      <Form onSubmit={handleSubmit}>
        <FormField
          label={__t(`${MSG_KEY}.fashion_styles.heading`)}
          labelType="highlighted"
          direction="column"
          showValidationIcon={false}
        >
          <div className="profile-boost-data__description">
            {__t(`${MSG_KEY}.fashion_styles.description`)}
          </div>
          {renderFashionStyles()}
        </FormField>
        <FormField
          label={__t(`${MSG_KEY}.fashion_categories.heading`)}
          labelType="highlighted"
          direction="column"
          showValidationIcon={false}
        >
          <div className="profile-boost-data__description">
            {__t(`${MSG_KEY}.fashion_categories.description`)}
          </div>
          {renderFashionCategories()}
        </FormField>
        <FormField
          label={__t(`${MSG_KEY}.fashion_sizes.heading`)}
          labelType="highlighted"
          direction="column"
          showValidationIcon={false}
        >
          <div className="profile-boost-data__description">
            {__t(`${MSG_KEY}.fashion_sizes.description`)}
          </div>
          <div
            onMouseEnter={(): void => handleFieldDescriptionShow('fashionSizes')}
            onMouseLeave={onFieldDescriptionHide}
          >
            {renderFashionSizes()}
          </div>
        </FormField>
        <FormField
          label={__t(`${MSG_KEY}.ethnic_groups.heading`)}
          labelType="highlighted"
          direction="column"
          showValidationIcon={false}
        >
          <div className="profile-boost-data__description">
            {__t(`${MSG_KEY}.ethnic_groups.description`)}
          </div>
          <div
            onMouseEnter={(): void => handleFieldDescriptionShow('ethnicGroups')}
            onMouseLeave={onFieldDescriptionHide}
          >
            {renderEthnicGroups()}
          </div>
        </FormField>
        {renderActions()}
      </Form>
    </div>
  );
};

export default ProfileBoostData;
